// Place history review: events about places that the loaders were not sure enough of
// to publish. Books read by a model, uncited lists and vague dates wait here as
// "needs review"; events that matched no place are "held". Served by ff-api at
// /api/admin/v2/area-events (read on cms.view, decide on cms.publish).
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  MapPinOff,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  areaEventKeys,
  useAreaEventQueueQuery,
  useAreaEventStatsQuery,
  useReviewMutation,
} from '@/client/api/areaEvents/areaEvents.query';
import {
  AreaEvent,
  AreaEventDate,
  ReviewDecision,
  ReviewStatus,
} from '@/client/types/areaEvents';
import { PageShell, Pagination, StatCard, fmtNum } from '../Integrations/integrationsUi';
import {
  AccessDenied,
  EmptyRow,
  ErrorNotice,
  Field,
  INPUT_CLASS,
  Th,
} from '../Scheduler/schedulerUi';

const BASE = '/admin/content/place-history';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'held', label: 'Held (no place)' },
  { value: 'needs_review,held', label: 'Needs review + held' },
  { value: 'published,approved', label: 'Live' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'duplicate', label: 'Duplicates' },
];

const SOURCE_LABEL: Record<string, string> = {
  archive_org: 'Old books',
  openstreetmap: 'OpenStreetMap',
  wikipedia_rulers: 'Ruler lists',
  wikipedia: 'Wikipedia timelines',
  wikipedia_article: 'Wikipedia place articles',
  wikipedia_known_for: 'Wikipedia (known for)',
  wikidata: 'Wikidata',
  district_profile: 'District profiles',
  gss_census: 'Census',
  news: 'News',
};

const STATUS_STYLE: Record<ReviewStatus, string> = {
  needs_review: 'bg-amber-50 text-amber-700 ring-amber-200',
  held: 'bg-gray-100 text-gray-600 ring-gray-200',
  published: 'bg-green-50 text-green-700 ring-green-200',
  approved: 'bg-green-50 text-green-700 ring-green-200',
  rejected: 'bg-red-50 text-red-700 ring-red-200',
  duplicate: 'bg-slate-100 text-slate-500 ring-slate-200',
  withdrawn: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A date printed only as exactly as the source gave it. */
const fmtEventDate = (d: AreaEventDate): string => {
  if (d.year == null) return d.month ? `Every ${MONTHS[d.month - 1]}` : 'No date';
  const year = d.year < 0 ? `${-d.year} BCE` : String(d.year);
  switch (d.precision) {
    case 'day':
      return `${d.day} ${MONTHS[(d.month ?? 1) - 1]} ${year}`;
    case 'month':
      return `${MONTHS[(d.month ?? 1) - 1]} ${year}`;
    case 'circa':
      return `c. ${year}`;
    case 'decade':
      return `${year}s`;
    case 'century':
      return `${Math.floor(d.year / 100) + 1}th century`;
    default:
      return d.yearEnd ? `${year}–${d.yearEnd}` : year;
  }
};

const StatusBadge: React.FC<{ status: ReviewStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
      STATUS_STYLE[status] ?? STATUS_STYLE.held
    }`}
  >
    {status.replace('_', ' ')}
  </span>
);

const placesText = (e: AreaEvent) =>
  e.places.length ? e.places.map((p) => p.name ?? `#${p.lid}`).join(', ') : 'No place matched';

/** Everything a reviewer needs to decide: the claim, the source's own words, the link. */
const EventDetail: React.FC<{
  event: AreaEvent | null;
  canDecide: boolean;
  busy: boolean;
  onDecide: (status: ReviewDecision) => void;
  onClose: () => void;
}> = ({ event, canDecide, busy, onDecide, onClose }) => (
  <Sheet open={!!event} onOpenChange={(open) => !open && onClose()}>
    <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
      <SheetHeader>
        <SheetTitle>{event?.title ?? 'Event'}</SheetTitle>
        <SheetDescription>
          {event ? `${fmtEventDate(event.date)} · ${placesText(event)}` : ''}
        </SheetDescription>
      </SheetHeader>
      {event && (
        <div className="mt-4 space-y-5 text-sm">
          {event.summary && <p className="text-gray-800">{event.summary}</p>}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <StatusBadge status={event.reviewStatus} />
            </Field>
            <Field label="Kind">{event.kind}</Field>
            <Field label="Source">{SOURCE_LABEL[event.origin] ?? event.origin}</Field>
            <Field label="Score">
              {event.score.toFixed(2)}
              {event.scoreComponents && (
                <span className="ml-2 text-xs text-gray-500">
                  source {event.scoreComponents.source} × date {event.scoreComponents.date} × place{' '}
                  {event.scoreComponents.place}
                </span>
              )}
            </Field>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">Places</p>
            {event.places.length === 0 ? (
              <p className="text-gray-500">
                No place matched. Approving it will not show it anywhere until it has one.
              </p>
            ) : (
              <ul className="mt-1 space-y-1">
                {event.places.map((p) => (
                  <li key={p.lid} className="text-gray-800">
                    {p.name ?? `#${p.lid}`}
                    <span className="ml-2 text-xs text-gray-500">
                      {p.basis.replace(/_/g, ' ')}, confidence {p.confidence}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">Sources</p>
            <ul className="mt-1 space-y-3">
              {event.sources.map((s) => (
                <li key={s.url} className="rounded-lg border border-gray-100 p-3">
                  {s.citation && <p className="mb-2 italic text-gray-700">{s.citation}</p>}
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 break-all text-blue-600 hover:underline"
                  >
                    {s.title || s.publisher || s.url}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                  <p className="mt-1 text-xs text-gray-500">
                    {[s.publisher, s.license].filter(Boolean).join(' · ')}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          {event.media.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {event.media.map((m) => (
                <a key={m.url} href={m.sourcePage ?? m.url} target="_blank" rel="noreferrer">
                  <img
                    src={m.thumbUrl ?? m.url}
                    alt={m.title ?? ''}
                    className="h-24 rounded-lg border border-gray-100 object-cover"
                  />
                </a>
              ))}
            </div>
          )}
          {canDecide && (
            <div className="flex gap-2 border-t border-gray-100 pt-4">
              <Button size="sm" disabled={busy} onClick={() => onDecide('approved')}>
                <Check className="mr-2 h-3.5 w-3.5" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => onDecide('rejected')}
              >
                <X className="mr-2 h-3.5 w-3.5" />
                Reject
              </Button>
              {event.reviewStatus !== 'needs_review' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onDecide('needs_review')}
                >
                  Back to review
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </SheetContent>
  </Sheet>
);

const AreaEventsReviewPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Content', href: '/admin/content/pages' },
    { label: 'Place history', href: BASE },
  ]);
  const queryClient = useQueryClient();
  const viewPerm = useHasPermission('cms.view');
  const decidePerm = useHasPermission('cms.publish');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canDecide = decidePerm.isSuperAdmin || decidePerm.hasPermission;

  const [status, setStatus] = useState('needs_review');
  const [origins, setOrigins] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState<AreaEvent | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  // A new filter starts from the first page with nothing ticked.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [status, origins, debounced, perPage]);

  const listQ = useAreaEventQueueQuery(
    { status, origins, q: debounced, limit: perPage, offset: (page - 1) * perPage },
    canView,
  );
  const statsQ = useAreaEventStatsQuery(canView);
  const review = useReviewMutation();

  const rows = listQ.data?.events ?? [];
  const total = listQ.data?.total ?? 0;
  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    for (const r of statsQ.data?.held ?? []) by[r.reviewStatus] = (by[r.reviewStatus] ?? 0) + r.events;
    return by;
  }, [statsQ.data]);
  const sources = useMemo(
    () => Array.from(new Set((statsQ.data?.held ?? []).map((r) => r.origin))).sort(),
    [statsQ.data],
  );

  const decide = (ids: number[], decision: ReviewDecision) =>
    review.mutate(
      { ids, status: decision },
      {
        onSuccess: () => {
          setSelected(new Set());
          setOpen(null);
        },
      },
    );
  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allTicked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const cols = canDecide ? 8 : 7;

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Place history" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Place history" description="">
        <AccessDenied code="cms.view" />
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Place history"
      description="History, people and facts about places, waiting for someone to check them before they are shown."
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: areaEventKeys.all })}
        >
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Refresh
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Needs review"
          value={fmtNum(counts.needs_review ?? 0)}
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          color="bg-amber-50"
        />
        <StatCard
          label="Held, no place"
          value={fmtNum(counts.held ?? 0)}
          icon={<MapPinOff className="h-5 w-5 text-gray-600" />}
          color="bg-gray-100"
        />
        <StatCard
          label="Live"
          value={fmtNum((counts.published ?? 0) + (counts.approved ?? 0))}
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          color="bg-green-50"
        />
        <StatCard
          label="Rejected"
          value={fmtNum(counts.rejected ?? 0)}
          icon={<Ban className="h-5 w-5 text-red-600" />}
          color="bg-red-50"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or summary"
            className={`${INPUT_CLASS} w-64 pl-8`}
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT_CLASS}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select value={origins} onChange={(e) => setOrigins(e.target.value)} className={INPUT_CLASS}>
          <option value="">Any source</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {SOURCE_LABEL[s] ?? s}
            </option>
          ))}
        </select>
        {canDecide && selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <Button
              size="sm"
              disabled={review.isPending}
              onClick={() => decide(Array.from(selected), 'approved')}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={review.isPending}
              onClick={() => decide(Array.from(selected), 'rejected')}
            >
              Reject
            </Button>
          </div>
        )}
      </div>

      {listQ.isError && <ErrorNotice error={listQ.error} onRetry={() => listQ.refetch()} />}
      {review.isError && <ErrorNotice error={review.error} />}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                {canDecide && (
                  <Th>
                    <input
                      type="checkbox"
                      aria-label="Select every row on this page"
                      checked={allTicked}
                      onChange={() =>
                        setSelected(allTicked ? new Set() : new Set(rows.map((r) => r.id)))
                      }
                    />
                  </Th>
                )}
                <Th>When</Th>
                <Th>What</Th>
                <Th>Place</Th>
                <Th>Source</Th>
                <Th>Kind</Th>
                <Th right>Score</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <EmptyRow cols={cols} loading>
                  {null}
                </EmptyRow>
              )}
              {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
                <EmptyRow cols={cols}>Nothing matches these filters</EmptyRow>
              )}
              {rows.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50/70"
                  onClick={() => setOpen(e)}
                >
                  {canDecide && (
                    <td className="px-4 py-2" onClick={(ev) => ev.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${e.title}`}
                        checked={selected.has(e.id)}
                        onChange={() => toggle(e.id)}
                      />
                    </td>
                  )}
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                    {fmtEventDate(e.date)}
                  </td>
                  <td className="max-w-md px-4 py-2">
                    <p className="font-medium text-gray-900">{e.title}</p>
                    {e.summary && <p className="truncate text-xs text-gray-500">{e.summary}</p>}
                  </td>
                  <td className="px-4 py-2 text-gray-700">{placesText(e)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                    {SOURCE_LABEL[e.origin] ?? e.origin}
                  </td>
                  <td className="px-4 py-2 text-gray-700">{e.kind}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                    {e.score.toFixed(2)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={e.reviewStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil(total / perPage))}
          total={total}
          perPage={perPage}
          onPage={setPage}
          onPerPage={setPerPage}
        />
      </div>

      <EventDetail
        event={open}
        canDecide={canDecide}
        busy={review.isPending}
        onDecide={(decision) => open && decide([open.id], decision)}
        onClose={() => setOpen(null)}
      />
    </PageShell>
  );
};

export default AreaEventsReviewPage;
